import React, { useState, useMemo, useEffect } from 'react';
import {
  useGetStudentsPaginatedQuery,
  useGetClassesQuery,
  useGetLocationGradesQuery,
} from '../../store/api/localApi';
import { Student } from '../../types';
import { XMarkIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';

interface StudentPickerProps {
  locationId: string;
  selectedStudentIds: string[];
  onChange: (studentIds: string[]) => void;
}

const PAGE_SIZE = 25;

/**
 * Searchable, paginated student picker for the Send-form modal. Locations can
 * have hundreds or (eventually) thousands of students, so we don't want to
 * render the full roster up front. Filters stack: search + grade + class.
 *
 * Selected students are kept across pagination by keeping the IDs in parent
 * state. Selected pills render at the top so the user can see and remove
 * picks even when the underlying page no longer shows them.
 */
const StudentPicker: React.FC<StudentPickerProps> = ({
  locationId,
  selectedStudentIds,
  onChange,
}) => {
  const [search, setSearch] = useState('');
  const [grade, setGrade] = useState('');
  const [classId, setClassId] = useState('');
  const [offset, setOffset] = useState(0);

  // Reset offset whenever filters change, otherwise we'd land on an empty page.
  useEffect(() => {
    setOffset(0);
  }, [search, grade, classId]);

  const { data, isFetching } = useGetStudentsPaginatedQuery({
    locationId,
    search: search.trim() || undefined,
    grade: grade || undefined,
    classId: classId || undefined,
    limit: PAGE_SIZE,
    offset,
  });

  const { data: grades } = useGetLocationGradesQuery(locationId);
  const { data: classes } = useGetClassesQuery(locationId);

  const students = data?.students ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

  // To render selected pills with names we need to remember students we've
  // seen on previous pages. Build a name map from the current page; fall
  // back to the ID if a student isn't on the current page.
  const [knownNames, setKnownNames] = useState<Record<string, string>>({});
  useEffect(() => {
    if (students.length === 0) return;
    setKnownNames((prev) => {
      const next = { ...prev };
      for (const s of students) next[s.id] = `${s.firstName} ${s.lastName}`;
      return next;
    });
  }, [students]);

  const toggleStudent = (id: string) => {
    onChange(
      selectedStudentIds.includes(id)
        ? selectedStudentIds.filter((sid) => sid !== id)
        : [...selectedStudentIds, id],
    );
  };

  const selectAllOnPage = () => {
    const pageIds = students.map((s) => s.id);
    const allOnPageSelected = pageIds.every((id) => selectedStudentIds.includes(id));
    onChange(
      allOnPageSelected
        ? selectedStudentIds.filter((id) => !pageIds.includes(id))
        : Array.from(new Set([...selectedStudentIds, ...pageIds])),
    );
  };

  const clearSelection = () => onChange([]);

  const allOnPageSelected = useMemo(
    () => students.length > 0 && students.every((s) => selectedStudentIds.includes(s.id)),
    [students, selectedStudentIds],
  );

  return (
    <div className="space-y-3">
      {/* Filter row */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <div className="relative">
          <MagnifyingGlassIcon className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            data-testid="student-picker-search"
            placeholder="Search by name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="block w-full pl-9 pr-3 py-2 rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
          />
        </div>
        <select
          data-testid="student-picker-grade"
          value={grade}
          onChange={(e) => setGrade(e.target.value)}
          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
        >
          <option value="">All grades</option>
          {(grades || []).map((g) => (
            <option key={g} value={g}>{g}</option>
          ))}
        </select>
        <select
          data-testid="student-picker-class"
          value={classId}
          onChange={(e) => setClassId(e.target.value)}
          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
        >
          <option value="">All classes</option>
          {(classes || []).map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
      </div>

      {/* Selected pills */}
      {selectedStudentIds.length > 0 && (
        <div className="flex items-start gap-2">
          <div className="flex-1 flex flex-wrap gap-1.5">
            {selectedStudentIds.map((id) => (
              <span
                key={id}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary-50 text-primary-700 text-xs"
              >
                {knownNames[id] || id.slice(0, 8)}
                <button
                  type="button"
                  onClick={() => toggleStudent(id)}
                  className="hover:text-primary-900"
                  aria-label="Remove"
                >
                  <XMarkIcon className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
          <button
            type="button"
            onClick={clearSelection}
            className="text-xs text-gray-500 hover:text-gray-700 whitespace-nowrap"
          >
            Clear ({selectedStudentIds.length})
          </button>
        </div>
      )}

      {/* Page header with select-all-on-page */}
      <div className="flex items-center justify-between text-sm">
        <span className="text-gray-500">
          {isFetching ? 'Loading…' : `${total} student${total === 1 ? '' : 's'}`}
        </span>
        {students.length > 0 && (
          <button
            type="button"
            onClick={selectAllOnPage}
            data-testid="student-picker-select-page"
            className="text-primary-600 hover:text-primary-700"
          >
            {allOnPageSelected ? 'Deselect page' : 'Select page'}
          </button>
        )}
      </div>

      {/* Student list */}
      <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg">
        {students.length === 0 && !isFetching ? (
          <div className="p-4 text-center text-gray-500 text-sm">No students match the filters.</div>
        ) : (
          <ul className="divide-y divide-gray-200">
            {students.map((s: Student) => {
              const checked = selectedStudentIds.includes(s.id);
              return (
                <li key={s.id} className="flex items-center p-2 hover:bg-gray-50">
                  <input
                    type="checkbox"
                    data-testid={`student-picker-row-${s.id}`}
                    checked={checked}
                    onChange={() => toggleStudent(s.id)}
                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="ml-3 text-sm text-gray-700">
                    {s.firstName} {s.lastName}
                  </span>
                  {s.grade && (
                    <span className="ml-2 text-xs text-gray-500">({s.grade})</span>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm">
          <button
            type="button"
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            disabled={offset === 0 || isFetching}
            className="px-2 py-1 text-gray-600 hover:text-gray-900 disabled:opacity-30"
          >
            ← Prev
          </button>
          <span className="text-gray-500">Page {currentPage} of {totalPages}</span>
          <button
            type="button"
            onClick={() => setOffset(offset + PAGE_SIZE)}
            disabled={currentPage >= totalPages || isFetching}
            className="px-2 py-1 text-gray-600 hover:text-gray-900 disabled:opacity-30"
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
};

export default StudentPicker;
